/**
 * Quimby Table column definitions for the Inspection Series listing.
 *
 * Maps ALL_SERIES_COLUMNS (from seriesColumnPolicy.ts) to TableColumn<SeriesRow>
 * descriptors. The selector pseudo-column is provided by the Table's `selectable`
 * prop and is NOT included here.
 *
 * Sticky column policy:
 *   - `id`      → columnType: 'primary'  (sticky left, after selector)
 *   - `actions` → columnType: 'action'   (sticky right)
 *
 * Column visibility is managed by the parent component (InspectionSeriesListing),
 * which filters this list by visibleColumnKeys before passing to Table.
 */
import React from 'react';
import I18n, { translate } from '@utils/i18n';
import { SeriesIdRenderer } from './renderers/SeriesIdRenderer';
import { SeriesLinkRenderer } from './renderers/SeriesLinkRenderer';
import { SeriesStatusRenderer } from './renderers/SeriesStatusRenderer';
import { SeriesActionsRenderer } from './renderers/SeriesActionsRenderer';
const NS = 'inspection_series.datatable';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Renders a nullable string/number field as a span or a dash. */
function NullableText({ item, dataKey }) {
    const value = item[dataKey];
    if (value === null || value === undefined || value === '')
        return React.createElement("span", null, "-");
    return React.createElement("span", null, String(value));
}
/**
 * All Series column definitions in display order.
 * Order matches ALL_SERIES_COLUMNS in seriesColumnPolicy.ts.
 */
export const ALL_SERIES_COLUMN_DEFS = [
    {
        key: 'id',
        label: translate('table.headers.id', { namespace: NS }),
        column: {
            dataKey: 'id',
            label: translate('table.headers.id', { namespace: NS }),
            width: '130px',
            columnType: 'primary',
            renderer: SeriesIdRenderer
        }
    },
    {
        key: 'referenceNumber',
        label: translate('table.headers.reference_number', { namespace: NS }),
        column: {
            dataKey: 'referenceNumber',
            label: translate('table.headers.reference_number', { namespace: NS }),
            renderer: NullableText
        }
    },
    {
        key: 'contractNumber',
        label: translate('table.headers.contract_number', { namespace: NS }),
        column: {
            dataKey: 'contractNumber',
            label: translate('table.headers.contract_number', { namespace: NS }),
            renderer: NullableText
        }
    },
    {
        key: 'building',
        label: I18n.t('generic.building'),
        column: {
            dataKey: 'building',
            label: I18n.t('generic.building'),
            width: '250px',
            renderer: SeriesLinkRenderer
        }
    },
    {
        key: 'account',
        label: I18n.t('generic.account'),
        column: {
            dataKey: 'account',
            label: I18n.t('generic.account'),
            width: '200px',
            renderer: SeriesLinkRenderer
        }
    },
    {
        key: 'frequencies',
        label: translate('table.headers.frequencies', { namespace: NS }),
        column: {
            dataKey: 'frequencies',
            label: translate('table.headers.frequencies', { namespace: NS }),
            renderer: NullableText
        }
    },
    {
        key: 'expirationDate',
        label: translate('table.headers.expiration_date', { namespace: NS }),
        column: {
            dataKey: 'expirationDate',
            label: translate('table.headers.expiration_date', { namespace: NS }),
            width: '140px',
            renderer: NullableText
        }
    },
    {
        key: 'systemAsset',
        label: translate('table.headers.system_asset', { namespace: NS }),
        column: {
            dataKey: 'systemAsset',
            label: translate('table.headers.system_asset', { namespace: NS }),
            renderer: NullableText
        }
    },
    {
        key: 'inspectionsCount',
        label: translate('table.headers.inspections_count', { namespace: NS }),
        column: {
            dataKey: 'inspectionsCount',
            label: translate('table.headers.inspections_count', { namespace: NS }),
            width: '100px',
            renderer: NullableText
        }
    },
    {
        key: 'status',
        label: translate('table.headers.status', { namespace: NS }),
        column: {
            dataKey: 'status',
            label: translate('table.headers.status', { namespace: NS }),
            width: '130px',
            renderer: SeriesStatusRenderer
        }
    },
    {
        key: 'autoExtend',
        label: translate('table.headers.auto_extend', { namespace: NS }),
        column: {
            dataKey: 'autoExtend',
            label: translate('table.headers.auto_extend', { namespace: NS }),
            renderer: NullableText
        }
    },
    {
        key: 'actions',
        label: ' ',
        column: {
            dataKey: 'actions',
            label: ' ',
            width: '60px',
            columnType: 'action',
            renderer: SeriesActionsRenderer
        }
    }
];
/**
 * Returns ordered TableColumn definitions for the given visible column keys.
 * Preserves the canonical ordering from ALL_SERIES_COLUMN_DEFS.
 */
export function buildVisibleSeriesColumns(visibleColumnKeys, onEditExpiration) {
    const keySet = new Set(visibleColumnKeys);
    return ALL_SERIES_COLUMN_DEFS.filter((def) => keySet.has(def.key)).map((def) => {
        if (def.key !== 'actions')
            return def.column;
        return Object.assign(Object.assign({}, def.column), { renderer: ({ item }) => (React.createElement(SeriesActionsRenderer, { item: item, onEditExpiration: onEditExpiration })) });
    });
}
/**
 * Returns ColumnVisibilityItem descriptors for the ColumnVisibilityControl,
 * filtered to the allowed keys from the column policy.
 */
export function buildSeriesColumnVisibilityItems(allowedKeys, requiredKeys) {
    const allowedSet = new Set(allowedKeys);
    const requiredSet = new Set(requiredKeys);
    return ALL_SERIES_COLUMN_DEFS.filter((def) => allowedSet.has(def.key)).map((def) => ({
        key: def.key,
        label: def.label,
        required: requiredSet.has(def.key)
    }));
}
